import React, { useEffect, useState } from 'react';

import {
  Box,
  Flex,
  Image as ChakraImage,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  Spinner,
} from '@chakra-ui/react';

import { notNull } from '../utils/core';
import Button from './Button';

const Image = ({
  as,
  children,
  src: source,
  srcs = [source],
  placeholder,
  placeholderSrc,
  expandable = false,
  expandedTitle = '',
  expandedComponent,
  loadingSpinner = false,
  style = {},
  onClick: clicked = () => {},
  imageProps = {},
  ...props
}) => {
  const [loading, setLoading] = useState(true);
  const [src, setSrc] = useState(source);
  const [srcList, setSrcList] = useState(srcs.filter(notNull));
  const [modalOpen, setModalOpen] = useState(false);
  const closeModal = () => setModalOpen(false);

  const handleClick = (...args) => {
    try {
      // open modal?
      if (expandable) { setModalOpen(true); }
    } finally {
      // pass it through
      clicked(...args);
    }
  };

  const tryNextSrc = () => {
    // try the next one...
    if (srcList.length) {
      const next = srcList.shift();
      setSrcList(srcList);
      setSrc(next);
    }
  };

  useEffect(() => {
    if (!src) {
      tryNextSrc();
    }
  }, [source]);

  const CurrentImage = ({ src: _src = src, ...rest }) => (
    <ChakraImage
      w="100%"
      h="100%"
      as={as}
      src={_src}
      {...imageProps}
      {...rest}
    >
      {children}
    </ChakraImage>
  );

  const Placeholder = () => {
    if (placeholder) {
      return placeholder(src);
    }
    return (<CurrentImage src={placeholderSrc || src} />);
  };

  const ExpandedComponent = () => {
    if (expandedComponent) {
      return expandedComponent(src);
    }
    return (<CurrentImage />);
  };

  return (
    <Box
      onClick={handleClick}
      style={{
        ...style,
        ...(expandable && { cursor: 'zoom-in' }),
      }}
      title={expandable ? 'View full size' : props.title}
      {...props}
    >
      {loading && (
        <Flex w="100%" h="100%" alignItems="center" justifyContent="center">
          <Placeholder />
          {loadingSpinner && (
            <Spinner size="xl" thickness="4px" color="#ddd" style={{ position: 'absolute', zIndex: 10 }} />
          )}
        </Flex>
      )}
      <ChakraImage
        w="100%"
        h="100%"
        as={as}
        src={src}
        {...imageProps}
        onError={tryNextSrc}
        onLoad={() => setLoading(false)}
        style={{
          ...style,
          ...(loading && { visibility: 'hidden' }),
        }}
      >
        {children}
      </ChakraImage>
      {expandable && (
        <Modal size="4xl" isOpen={modalOpen} onClose={closeModal} scrollBehavior="inside">
          <ModalOverlay />
          <ModalContent display="flex" alignItems="center" justifyContent="center" mh={800} pt="16px">
            <ModalHeader
              textStyle="h1"
              fontSize={['24px', '24px', '36px']}
              my="16px"
              fontWeight="bold"
              p="0"
              textAlign="center"
            >
              {expandedTitle}
            </ModalHeader>
            <ModalBody display="flex" alignItems="center" justifyContent="center" py="0" px="16px">
              <ExpandedComponent />
            </ModalBody>
            <ModalFooter mb="16px" p="0">
              <Button
                my="16px"
                onClick={closeModal}
                color="white"
              >
                Close
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>
      )}
    </Box>
  );
};

export default Image;
